import { App, Button, Checkbox, Modal, Select, Skeleton, Space } from "antd"

import { EyeOutlined, LoginOutlined, TeamOutlined } from "@ant-design/icons"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import Table, { ColumnsType } from "antd/es/table"
import { useContext, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ContainerFull } from "../../components"
import { refreshSessionUser, ShowMessagePage } from "../../config"
import { UserDispatchContext } from "../../context"
import { 
    getUsers, 
    impersonate, 
    toggleActiveUser, 
    getUserGroups, 
    assignUserToGroup,
    UserGroup 
} from "../../endpoints"
import { UserDetailsInterface } from "../../types"

export function Users(): JSX.Element {
    const [selectedRow, setSelectedRow] = useState<number>(-1)
    const [groupModalVisible, setGroupModalVisible] = useState(false)
    const [selectedUser, setSelectedUser] = useState<UserDetailsInterface | null>(null)
    const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null)
    
    const dispatch = useContext(UserDispatchContext)
    const queryClient = useQueryClient()
    const { modal } = App.useApp()
    
    const { isPending, isError, data, error, isSuccess } = useQuery({
        queryKey: ["getallusers"],
        queryFn: getUsers,
    })
    
    const { data: groupsData } = useQuery({
        queryKey: ["getUserGroups"],
        queryFn: getUserGroups,
    })
    
    const mutation = useMutation({
        mutationFn: toggleActiveUser,
        onSuccess: (data) => {
            // Invalidate and refetch
            queryClient.invalidateQueries({ queryKey: ["getallusers"] })
            // console.log(data)
            if (data.code !== 1000) modal.warning({ content: "failed to update user status" })
        },
        onError: (error) => {
            modal.error({ content: error.message })
        },
    })

    const groupAssignMutation = useMutation({
        mutationFn: assignUserToGroup,
        onSuccess: (data) => {
            if (data.code === 1000) {
                modal.success({ content: data.message })
                queryClient.invalidateQueries({ queryKey: ["getallusers"] })
                setGroupModalVisible(false)
                setSelectedUser(null)
                setSelectedGroupId(null)
            } else {
                modal.error({ content: data.message })
            }
        },
        onError: (error) => {
            modal.error({ content: "Failed to assign user to group" })
        },
    })

    const handleGroupAssignment = (user: UserDetailsInterface) => {
        setSelectedUser(user)
        setSelectedGroupId(user.groupid || null)
        setGroupModalVisible(true)
    }

    const handleGroupAssignmentConfirm = () => {
        if (selectedUser) {
            groupAssignMutation.mutate({
                userId: selectedUser.id,
                groupId: selectedGroupId,
            })
        }
    }

    const navigate = useNavigate()
    if (isPending) {
        return <Skeleton />
    }

    if (isError) {
        //  return <div>error</div>
        return ShowMessagePage("Error", error.message)
    }
    if (isSuccess) {
        // console.log(data.users)
    }

    const columns: ColumnsType<UserDetailsInterface> = [
        {
            title: "Details",
            dataIndex: "id",
            align: "center",
            render: (value, record, index) => {
                return (
                    <EyeOutlined
                        style={{ color: "blue" }}
                        onClick={async () => {
                            navigate("/admin/user/" + record.id)
                        }}
                    />
                )
            },
        },
        {
            title: "Become",
            dataIndex: "id",
            align: "center",
            render: (value, record, index) => {
                return (
                    <LoginOutlined
                        style={{ color: "blue" }}
                        onClick={async () => {
                            // console.log('value clicked:'+value)
                            const result = await impersonate({ userId: value })
                            // consolelog("result", result)
                            if (result.code === 1000) {
                                refreshSessionUser(result.userInSession, dispatch)
                                // console.log("impersonated user", result.userInSession)
                                window.location.href = "/myspace/orders"
                            }
                        }}
                    />
                )
            },
        },

        {
            title: "Active",
            dataIndex: "active",
            align: "center",
            render: (value, record, index) => {
                return (
                    <Checkbox
                        checked={value}
                        onChange={(e) => {
                            mutation.mutate({
                                userId: record.id,
                            })
                        }}
                    />
                )
            },
        },
        {
            title: "ID",
            dataIndex: "id",
            align: "center",
            sorter: (a, b) => a.id - b.id,
            // defaultSortOrder: 'ascend',
            render: (text, record, index) => {
                return <span>{record.id}</span>
            },
        },
        {
            title: "First Name",
            dataIndex: "firstName",
            align: "center",
            sorter: (a, b) => (a.firstName || '').localeCompare(b.firstName || ''),
            render: (text, record, index) => {
                return <span>{record.firstName}</span>
            },
        },
        {
            title: "Last Name",
            dataIndex: "lastName",
            sorter: (a, b) => (a.lastName || '').localeCompare(b.lastName || ''),
            render: (text, record, index) => {
                return <span>{record.lastName}</span>
            },
        },

        {
            title: "Email",
            dataIndex: "email",
            align: "center",
            sorter: (a, b) => (a.email || '').localeCompare(b.email || ''),
            render: (text, record, index) => {
                return <span>{record.email}</span>
            },
        },
        {
            title: "Role",
            dataIndex: "userRole",
            align: "center",
            sorter: (a, b) => a.userRole.localeCompare(b.userRole),
            render: (text, record, index) => {
                return <span>{record.userRole}</span>
            },
        },
        {
            title: "verified",
            dataIndex: "verified",
            align: "center",
            sorter: (a, b) => (a.verified === b.verified ? 0 : a.verified ? -1 : 1),
            render: (text, record, index) => {
                return <span>{record.verified ? "Yes" : "No"}</span>
            },
        },
        {
            title: "Last Login",
            dataIndex: "lastLogin",
            align: "center",
            sorter: (a, b) => {
                const aTime = a.lastLogin ? new Date(a.lastLogin).getTime() : 0
                const bTime = b.lastLogin ? new Date(b.lastLogin).getTime() : 0
                return aTime - bTime
                // return new Date(a.lastLogin || '').getTime() - new Date(b.lastLogin || '').getTime()
            },
            defaultSortOrder: 'descend',
            render: (text, record, index) => {

                if (!record.lastLogin) {
                    return <span>-</span>
                }
                const localDate = new Date(record.lastLogin).toLocaleString()
                return <span>{localDate}</span>
            },
        },
        {
            title: "Created",
            dataIndex: "ts",
            align: "center",
            sorter: (a, b) => new Date(a.ts || '').getTime() - new Date(b.ts || '').getTime(),
            render: (text, record, index) => {
                return <span>{record.ts}</span>
            },
        },
        {
            title: "Active",
            dataIndex: "active",
            align: "center",
            sorter: (a, b) => (a.active === b.active ? 0 : a.active ? -1 : 1),
            render: (text, record, index) => {
                return <span>{record.active ? "Yes" : "No"}</span>
            },
        },
        {
            title: "Group",
            dataIndex: "group_name",
            align: "center",
            sorter: (a, b) => (a.group_name || '').localeCompare(b.group_name || ''),
            render: (text, record, index) => {
                return (
                    <Space>
                        <span>{record.group_name || '--'}</span>
                        <Button
                            type="link"
                            size="small"
                            icon={<TeamOutlined />}
                            onClick={(e) => {
                                e.stopPropagation()
                                handleGroupAssignment(record)
                            }}
                        />
                    </Space>
                )
            },
        },
        {
            title: "Receive Group Order Notifications",
            dataIndex: "receive_group_order_notifications",
            align: "center",
            sorter: (a, b) => {
                const aVal = a.receive_group_order_notifications ?? false
                const bVal = b.receive_group_order_notifications ?? false
                return aVal === bVal ? 0 : aVal ? -1 : 1
            },
            render: (text, record, index) => {
                return <span>{record.receive_group_order_notifications ? "Yes" : "No"}</span>
            },
        },
        {
            title: "View Group Orders",
            dataIndex: "view_group_orders",
            align: "center",
            sorter: (a, b) => {
                const aVal = a.view_group_orders ?? false
                const bVal = b.view_group_orders ?? false
                return aVal === bVal ? 0 : aVal ? -1 : 1
            },
            render: (text, record, index) => {
                return <span>{record.view_group_orders ? "Yes" : "No"}</span>
            },
        },
        {
            title: "View Group Invoices",
            dataIndex: "view_group_invoices",
            align: "center",
            sorter: (a, b) => {
                const aVal = a.view_group_invoices ?? false
                const bVal = b.view_group_invoices ?? false
                return aVal === bVal ? 0 : aVal ? -1 : 1
            },
            render: (text, record, index) => {
                return <span>{record.view_group_invoices ? "Yes" : "No"}</span>
            },
        },
        {
            title: "Institution",
            dataIndex: "institition",
            align: "center",
            sorter: (a, b) => (a.institution || '').localeCompare(b.institution || ''),
            render: (text, record, index) => {
                return <span>{record.institution}</span>
            },
        },
        {
            title: "Phone",
            dataIndex: "phone",
            align: "center",
            render: (text, record, index) => {
                return <span>{record.phone}</span>
            },
        },
        {
            title: "Cell",
            dataIndex: "cell",
            render: (text, record, index) => {
                return <span>{record.cell}</span>
            },
        },
        {
            title: "Address1",
            dataIndex: "address",
            align: "center",
            render: (text, record, index) => {
                return <span>{record.address?.line1}</span>
            },
        },
        {
            title: "Address2",
            dataIndex: "address",
            align: "center",
            render: (text, record, index) => {
                return <span>{record.address?.line2}</span>
            },
        },
        {
            title: "City",
            dataIndex: "address",
            align: "center",
            sorter: (a, b) => (a.address?.city || '').localeCompare(b.address?.city || ''),
            render: (text, record, index) => {
                return <span>{record.address?.city}</span>
            },
        },
        {
            title: "State",
            dataIndex: "address",
            align: "center",
            sorter: (a, b) => (a.address?.state || '').localeCompare(b.address?.state || ''),
            render: (text, record, index) => {
                return <span>{record.address?.state}</span>
            },
        },
        {
            title: "Country",
            dataIndex: "address",
            align: "center",
            render: (text, record, index) => {
                return <span>{record.address?.country}</span>
            },
        },
        {
            title: "Zipcode",
            dataIndex: "address",
            align: "center",
            sorter: (a, b) => (a.address?.zip || '').localeCompare(b.address?.zip || ''),
            render: (text, record, index) => {
                return <span>{record.address?.zip}</span>
            },
        },
        
        
    ]

    return (
        <div>
            <ContainerFull>
                <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <h2>Users</h2>
                    <Button
                        type="primary"
                        icon={<TeamOutlined />}
                        onClick={() => navigate("/admin/usergroups")}
                    >
                        Manage User Groups
                    </Button>
                </div>
                <Table
                    dataSource={data?.users?.map((row) => {
                        return { ...row, key: row.id }
                    })}
                    rowClassName={(record, index) =>
                        index === selectedRow ? "highlighted-table-row" : "regular-table-row"
                    }
                    columns={columns}
                    pagination={{ position: ["bottomLeft"], pageSize: 100 }}
                    size="middle"
                    onRow={(record, index) => {
                        return {
                            onClick: () => {
                                if (index === selectedRow) {
                                    setSelectedRow(-1)
                                } else {
                                    setSelectedRow(index ?? -1)
                                }
                            },
                        }
                    }}
                />
                
                <Modal
                    title="Assign User to Group"
                    open={groupModalVisible}
                    onOk={handleGroupAssignmentConfirm}
                    onCancel={() => {
                        setGroupModalVisible(false)
                        setSelectedUser(null)
                        setSelectedGroupId(null)
                    }}
                    confirmLoading={groupAssignMutation.isPending}
                >
                    <p>
                        Assign <strong>{selectedUser?.firstName} {selectedUser?.lastName}</strong> to a group:
                    </p>
                    <Select
                        style={{ width: "100%" }}
                        placeholder="Select a group"
                        value={selectedGroupId}
                        onChange={(value) => setSelectedGroupId(value)}
                        allowClear
                    >
                        {groupsData?.groups?.map((group: UserGroup) => (
                            <Select.Option key={group.id} value={group.id}>
                                {group.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Modal>
            </ContainerFull>
        </div>
    )
}
